import { Alert, Badge, EmptyState, LoadMore, Skeleton } from '@ideon-api/ideon-ui'
import { ABSENT_VALUE, displayValue, entryDelta, entryTitle, fieldLabel, type Delta } from '../../lib/audit-delta'
import { useChangeSetEntries, type ChangeSetEntriesArgs, type ChangeSetEntry } from '../../lib/use-change-set-entries'

type Props = {
  slug: string
  changeSetId: string
  permissionSetKey: string | null
  resourceType?: ChangeSetEntriesArgs['resourceType']
  since?: string
}

const ACTION_LABELS: Record<string, string> = { create: 'Added', update: 'Changed', delete: 'Removed', archive: 'Archived', download: 'Downloaded' }

function Label({ field }: { field: string }) {
  const label = fieldLabel(field)
  return label ? <span className="text-ideon-gray-70">{label}:</span> : null
}

// oneSided: a create or delete, where the absent side is the whole story. An update keeps before → after.
export function DeltaLine({ delta, oneSided = true }: { delta: Delta; oneSided?: boolean }) {
  if ('added' in delta) {
    return (
      <span className="flex flex-wrap items-center gap-1 text-sm">
        <Label field={delta.field} />
        {delta.added.map((v) => (
          <Badge key={`+${typeof v}:${v}`} variant="success" dot={false} uppercase={false}>{`+${v}`}</Badge>
        ))}
        {delta.removed.map((v) => (
          <Badge key={`-${typeof v}:${v}`} variant="error" dot={false} uppercase={false}>{`−${v}`}</Badge>
        ))}
      </span>
    )
  }
  // An added or removed record has only one side worth showing.
  const value =
    !oneSided ||
    (delta.before !== ABSENT_VALUE && delta.after !== ABSENT_VALUE)
      ? `${displayValue(delta.before)} → ${displayValue(delta.after)}`
      : delta.before === ABSENT_VALUE
      ? displayValue(delta.after)
      : delta.after === ABSENT_VALUE
        ? displayValue(delta.before)
        : `${displayValue(delta.before)} → ${displayValue(delta.after)}`
  return (
    <span className="flex flex-wrap items-center gap-1 text-sm">
      <Label field={delta.field} />
      <span>{value}</span>
    </span>
  )
}

function EntryItem({ entry }: { entry: ChangeSetEntry }) {
  const resourceType = entry.resourceType ?? ''
  const diff = entry.diff ?? {}
  const deltas = entryDelta(diff, resourceType)

  return (
    <li className="flex flex-col gap-1.5 py-3">
      <div className="flex items-center gap-2">
        <Badge variant="neutral" dot={false} uppercase={false}>
          {ACTION_LABELS[entry.action ?? ''] ?? 'Changed'}
        </Badge>
        <span className="font-medium">{entryTitle({ resourceType, diff })}</span>
      </div>
      {deltas.length > 0 && (
        <div className="flex flex-col gap-1">
          {deltas.map((d) => (
            <DeltaLine key={d.field} delta={d} oneSided={entry.action !== 'update'} />
          ))}
        </div>
      )}
    </li>
  )
}

export function ChangeSetEntries({ slug, changeSetId, permissionSetKey, resourceType, since }: Props) {
  const state = useChangeSetEntries(slug, { changeSetId, permissionSetKey, resourceType, since })
  if (state.status === 'loading') {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }
  if (state.status === 'error') return <Alert variant="error">{state.message}</Alert>
  const lastPage = state.meta.page >= state.meta.totalPages

  return (
    <div className="flex flex-col gap-2">
      {state.data.length === 0 && lastPage && <EmptyState heading="No changes to show" />}
      <ul className="flex flex-col divide-y divide-ideon-gray-40">
        {state.data.map((entry) => (
          <EntryItem key={entry.id} entry={entry} />
        ))}
      </ul>
      {state.loadError && <Alert variant="error">{state.loadError}</Alert>}
      {!(state.data.length === 0 && lastPage) && (
      <LoadMore
        currentPage={state.meta.page}
        pageSize={state.meta.perPage}
        totalCount={state.meta.totalCount}
        totalPages={state.meta.totalPages}
        onLoadMore={state.loadMore}
        isLoading={state.loadingMore}
      />
      )}
    </div>
  )
}
